import { withMaintenanceRetry } from "@/lib/maintenance-retry";
import { index as nodesIndex } from "@/routes/nodes";
import type { NodeModel } from "@/types/models";

type StreamTreeNodesProgress = {
  loaded: number;
  total?: number;
  percentage?: number;
};

export type StreamTreeNodesOptions = {
  url?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  params?: Record<string, string | number | boolean | null | undefined>;
  expectedTotal?: number;
  onProgress?: (progress: StreamTreeNodesProgress) => void;
};

const requestUrl = (url: string, params?: StreamTreeNodesOptions["params"]) => {
  const resolved = new URL(url, globalThis.location.origin);

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== null && value !== undefined) {
      resolved.searchParams.set(key, String(value));
    }
  }

  return resolved;
};

const publishProgress = (
  loaded: number,
  total: number | undefined,
  onProgress: StreamTreeNodesOptions["onProgress"],
) => {
  const percentage = total && total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : undefined;

  onProgress?.({ loaded, total, percentage });
};

// Keeps the server's JSON body, such as a refusal's `reason`: this runs in the tree worker too, which cannot navigate,
// so main-thread callers act on it.
const requestError = (message: string, status: number, data: unknown, url: string) => {
  const error = new Error(message) as Error & {
    response: { status: number; data: unknown };
    config: { url: string };
  };
  error.response = { status, data };
  error.config = { url };

  return error;
};

const nodesFromJsonBody = (body: unknown, url: string): NodeModel[] => {
  const nodes = typeof body === "object" && body !== null ? (body as { nodes?: unknown }).nodes : undefined;

  if (!Array.isArray(nodes)) {
    throw new Error(`Tree nodes response from ${url} has no nodes list.`);
  }

  return nodes as NodeModel[];
};

export const streamTreeNodes = async ({
  url = nodesIndex.url(),
  headers,
  signal,
  params,
  expectedTotal,
  onProgress,
}: StreamTreeNodesOptions = {}): Promise<NodeModel[]> => {
  const resolvedUrl = requestUrl(url, params);
  return withMaintenanceRetry(
    (retrySignal) =>
      fetch(resolvedUrl, {
        credentials: "same-origin",
        headers: {
          Accept: "application/x-ndjson, application/json",
          // Marks the request as the app's own, so Laravel does not record the stream as the page to return to.
          "X-Requested-With": "XMLHttpRequest",
          ...headers,
        },
        signal: retrySignal,
      }),
    { signal },
    async (response) => {
      if (!response.ok) {
        const data = await response.json().catch(() => undefined);

        throw requestError(
          `Tree nodes request failed with HTTP ${response.status}.`,
          response.status,
          data,
          resolvedUrl.toString(),
        );
      }

      const contentType = response.headers.get("content-type") ?? "";
      const isNdjson = contentType.includes("application/x-ndjson");

      // A redirect to a page, usually the sign-in page after the session expired, reaches fetch as HTML with 200 (AWRAQ-2M3).
      // It carries 401, so the tree handles it as an expected refusal instead of parsing HTML.
      if (!isNdjson && !contentType.includes("application/json")) {
        throw requestError(
          `Tree nodes request was answered with ${contentType || "no content type"}.`,
          401,
          undefined,
          resolvedUrl.toString(),
        );
      }

      if (!isNdjson) {
        const nodes = nodesFromJsonBody(await response.json(), resolvedUrl.toString());
        publishProgress(nodes.length, nodes.length, onProgress);

        return nodes;
      }

      const nodes: NodeModel[] = [];
      const decoder = new TextDecoder();
      let buffer = "";
      let lastPercentage = -1;
      const reportParsedNodes = () => {
        const percentage =
          expectedTotal && expectedTotal > 0 ? Math.min(100, Math.round((nodes.length / expectedTotal) * 100)) : -1;

        if (percentage !== lastPercentage) {
          lastPercentage = percentage;
          publishProgress(nodes.length, expectedTotal, onProgress);
        }
      };
      const consumeLine = (line: string) => {
        const trimmed = line.trim();

        if (!trimmed) {
          return;
        }

        const record = JSON.parse(trimmed) as { node: NodeModel };
        nodes.push(record.node);
        reportParsedNodes();
      };

      if (!response.body) {
        for (const line of (await response.text()).split("\n")) {
          consumeLine(line);
        }
      } else {
        const reader = response.body.getReader();

        while (true) {
          const { done, value } = await reader.read();
          buffer += decoder.decode(value, { stream: !done });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            consumeLine(line);
          }

          if (done) {
            break;
          }
        }

        consumeLine(buffer);
      }

      if (lastPercentage !== 100) {
        publishProgress(nodes.length, nodes.length, onProgress);
      }

      return nodes;
    },
  );
};
